import React from "react";
import {
  Card,
  CardContent,
  Typography,
  Chip,
  Box,
  Tooltip,
  Paper,
} from "@mui/material";
import {
  FiberManualRecord as FiberManualRecordIcon,
  // LocationOn as LocationOnIcon,
  // Leaderboard as LeaderboardIcon,
} from "@mui/icons-material";
// import { Link } from "react-router-dom";
import { backend_url } from "../../App";

export default function Camera({ content, updateMapCenter }) {
  let latest = `${backend_url}/media/${content.latest_event}`;
  // let id = content.id;

  // Helper function to get relative time
  const getRelativeTime = (dateString) => {
    if (!dateString) return "Never";

    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);
    const diffInMinutes = Math.floor(diffInSeconds / 60);
    const diffInHours = Math.floor(diffInMinutes / 60);
    const diffInDays = Math.floor(diffInHours / 24);
    const diffInMonths = Math.floor(diffInDays / 30);
    const diffInYears = Math.floor(diffInDays / 365);

    if (diffInSeconds < 60) {
      return `${diffInSeconds} second${diffInSeconds !== 1 ? "s" : ""} ago`;
    } else if (diffInMinutes < 60) {
      return `${diffInMinutes} minute${diffInMinutes !== 1 ? "s" : ""} ago`;
    } else if (diffInHours < 24) {
      return `${diffInHours} hour${diffInHours !== 1 ? "s" : ""} ago`;
    } else if (diffInDays < 30) {
      return `${diffInDays} day${diffInDays !== 1 ? "s" : ""} ago`;
    } else if (diffInMonths < 12) {
      return `${diffInMonths} month${diffInMonths !== 1 ? "s" : ""} ago`;
    } else {
      return `${diffInYears} year${diffInYears !== 1 ? "s" : ""} ago`;
    }
  };

  const getDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    const hours = date.getHours();
    date.setHours(hours + 5);
    const updatedDate = date.toISOString().slice(0, 16).replace("T", " ");
    return updatedDate;
  };

  // Helper function to get camera status
  const getCameraStatus = (camera) => {
    const isLive = camera.live === true;
    const lastReportedAt = camera.last_reported_at;

    if (!isLive) {
      return { label: "UNARMED", color: "error", iconColor: "#d32f2f" };
    }

    if (!lastReportedAt) {
      return { label: "OFFLINE", color: "warning", iconColor: "#ed6c02" };
    }

    const lastReported = new Date(lastReportedAt);
    const now = new Date();
    const diffInHours = (now - lastReported) / (1000 * 60 * 60);

    if (diffInHours <= 1) {
      return { label: "ARMED", color: "success", iconColor: "#2e7d32" };
    } else {
      return { label: "OFFLINE", color: "warning", iconColor: "#ed6c02" };
    }
  };

  const status = getCameraStatus(content);
  const statusColor =
    status.color === "success"
      ? "success"
      : status.color === "warning"
        ? "warning"
        : "error";

  // const scrollToTop = () => {
  //   window.scrollTo(0, 0);
  // };

  return (
    <Card
      sx={{
        width: "100%",
        maxWidth: 280,
        minWidth: 200,
        borderRadius: 3,
        boxShadow: "0 4px 15px rgba(0,0,0,0.08)",
        transition: "transform 0.3s ease, box-shadow 0.3s ease",
        "&:hover": {
          transform: "translateY(-4px)",
          boxShadow: "0 8px 25px rgba(0,0,0,0.12)",
        },
        borderLeft: `4px solid ${status.iconColor}`,
        position: "relative",
        overflow: "visible",
      }}
    >
      {/* Live Indicator
      {content.live && (
        <Tooltip title="Live View" placement="top">
          <Box
            sx={{
              position: "absolute",
              top: -8,
              right: -8,
              zIndex: 1,
              bgcolor: "white",
              borderRadius: "50%",
              p: 0.5,
              boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
              cursor: "pointer",
              "&:hover": {
                transform: "scale(1.1)",
              },
              transition: "transform 0.2s ease",
            }}
            onClick={() => {
              window.location.href = `/live/${id}`;
            }}
          ></Box>
        </Tooltip>
      )} */}
      <CardContent sx={{ pr: 2 }}>
        {/* Header with Status */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "start",
            mb: 1,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.3 }}>
            <FiberManualRecordIcon
              sx={{
                color: status.iconColor,
                fontSize: "12px",
                animation:
                  status.label === "ARMED" ? "pulse 1.5s infinite" : "none",
                "@keyframes pulse": {
                  "0%": { opacity: 1 },
                  "50%": { opacity: 0.3 },
                  "100%": { opacity: 1 },
                },
              }}
            />
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 600,
                fontSize: "0.775rem",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                maxWidth: 120,
              }}
            >
              {content.description}
            </Typography>
          </Box>
          <Chip
            label={status.label}
            size="small"
            color={statusColor}
            sx={{
              height: "20px",
              ml: 1,
              fontSize: "0.6rem",
              fontWeight: "bold",
              "& .MuiChip-label": { px: 1, py: 0.5 },
            }}
          />
        </Box>

        {/* Image */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            my: 1,
          }}
        >
          <Box
            component="img"
            src={latest}
            alt="Camera feed"
            sx={{
              width: 150,
              height: 150,
              borderRadius: 2,
              objectFit: "cover",
              bgcolor: "#f5f5f5",
            }}
            onError={(e) => {
              e.target.src = "/video.png";
            }}
          />
        </Box>

        {/* Last Event */}
        <Paper
          variant="outlined"
          sx={{
            display: "flex",
            justifyContent: "center",
            py: 0.5,
            px: 1,
            my: 1,
            borderRadius: 1,
            borderColor: "#e0e0e0",
          }}
        >
          <Typography
            variant="caption"
            sx={{ fontWeight: 500, fontSize: "10px" }}
          >
            Last Event: {getDate(content.last_reported_at)}
          </Typography>
        </Paper>

        {/* Last Ping */}
        <Tooltip
          title={`Last ping: ${getDate(content.last_reported_at)}`}
          arrow
        >
          <Typography
            variant="caption"
            sx={{
              display: "block",
              textAlign: "center",
              color: status.iconColor,
              fontWeight: "bold",
              // mb: 1,
            }}
          >
            Last Ping: {getRelativeTime(content.last_reported_at)}
          </Typography>
        </Tooltip>

        {/* Actions */}
        {/* <Box sx={{ display: "flex", justifyContent: "space-around", gap: 1 }}>
          <Tooltip title="View Location" placement="top">
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                px: 1.5,
                py: 0.5,
                borderRadius: 2,
                border: "1px solid #ddd",
                cursor: "pointer",
                "&:hover": {
                  bgcolor: "#f5f5f5",
                },
                transition: "all 0.2s ease",
              }}
              onClick={() => {
                scrollToTop();
                if (updateMapCenter) {
                  updateMapCenter(content.latitude, content.longitude);
                }
              }}
            >
              <LocationOnIcon sx={{ fontSize: "0.9rem", color: "#555" }} />
              <Typography
                variant="caption"
                sx={{ color: "#555", fontSize: "0.7rem" }}
              >
                Location
              </Typography>
            </Box>
          </Tooltip>

          <Tooltip title="View Statistics" placement="top">
            <Box
              component={Link}
              to={`/statistics/${content.id}`}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                px: 1.5,
                py: 0.5,
                borderRadius: 2,
                border: "1px solid #ddd",
                cursor: "pointer",
                textDecoration: "none",
                "&:hover": {
                  bgcolor: "#f5f5f5",
                },
                transition: "all 0.2s ease",
              }}
            >
              <LeaderboardIcon sx={{ fontSize: "0.9rem", color: "#555" }} />
              <Typography
                variant="caption"
                sx={{ color: "#555", fontSize: "0.7rem" }}
              >
                Statistics
              </Typography>
            </Box>
          </Tooltip>
        </Box> */}
      </CardContent>
    </Card>
  );
}
